<template>
  <b-container fluid>
    <div v-if="userObject && userObject.isSysAdmin">
      <div class="d-flex align-items-center justify-content-between mt-4 mb-3">
        <h3 class="mb-0">Snippets</h3>
        <div class="d-flex align-items-center">
          <router-link to="/admin/elements" class="mr-3">Elements</router-link>
          <b-button variant="primary" @click="openCreateModal">
            {{ $t('buttons.create') }}
          </b-button>
        </div>
      </div>
      <admin-snippets-table
        v-if="!$apollo.loading"
        :snippets="snippets"
        @edit-snippet="openEditModal"
        @duplicate-snippet="openDuplicateModal"
        @delete-snippet="confirmDelete"
      />
      <admin-snippet-modal
        :visible="modalOpen"
        :snippet="selectedSnippet"
        :duplicate-from="duplicateSource"
        :groups="groupOptionsList"
        @change="modalOpen = $event"
        @saved="onSnippetSaved"
      />
    </div>
    <div v-else-if="userObject" class="pt-5">
      <p class="text-center" v-html="$t('adminAccountManagement.notAllowed')"></p>
    </div>
  </b-container>
</template>

<script>
import GetAllSnippetsV2ForAdmin from '@/graphQlQueries/queries/getAllSnippetsV2ForAdmin';
import GetAllAccountsForAdmin from '@/graphQlQueries/queries/getAllAccountsForAdmin';
import DeleteSnippetV2 from '@/graphQlQueries/mutations/deleteSnippetV2';
import SetLoadingIndicator from '@/graphQlQueries/mutations/setLoadingIndicatorInCache';
import SetPageTypeInCache from '@/graphQlQueries/mutations/setPageTypeInCache';
import SetRunBreadcrumbQueryInCache from '@/graphQlQueries/mutations/setRunBreadcrumbQueryInCache';
import SetShowHeaderInCache from '@/graphQlQueries/mutations/setShowHeaderInCache';
import UserDataAndAccess from '@/mixins/userDataAndAccess';

export default {
  name: 'AdminSnippets',
  mixins: [UserDataAndAccess],
  components: {
    AdminSnippetsTable: () => import('@/components/settings/admin/AdminSnippetsTable.vue'),
    AdminSnippetModal: () => import('@/components/settings/admin/AdminSnippetModal.vue'),
  },
  data: () => ({
    pageType: 'admin-settings',
    snippets: [],
    modalOpen: false,
    selectedSnippet: null,
    duplicateSource: null,
    groupOptionsList: [],
  }),
  apollo: {
    snippets: {
      query: GetAllSnippetsV2ForAdmin,
      update: (data) => data.snippets_v2,
      fetchPolicy: 'network-only',
      skip() {
        return !this.userObject?.userId || !this.userObject?.isSysAdmin;
      },
    },
    groupOptionsList: {
      query: GetAllAccountsForAdmin,
      update: (data) => data.groups.map((g) => ({ group_id: g.group_id, name: g.name })),
      skip() {
        return !this.userObject?.userId || !this.userObject?.isSysAdmin;
      },
    },
  },
  watch: {
    userObject() {
      if (this.userObject && this.userObject.userId) {
        this.$apollo.mutate({
          mutation: SetLoadingIndicator,
          variables: { isIndicatorLoading: false },
        });
      }
    },
  },
  created() {
    this.$apollo.mutate({
      mutation: SetLoadingIndicator,
      variables: { isIndicatorLoading: true },
    });
    this.$apollo.mutate({
      mutation: SetPageTypeInCache,
      variables: { type: 'admin-settings' },
    });
    this.$apollo.mutate({
      mutation: SetRunBreadcrumbQueryInCache,
      variables: { runQuery: true },
    });
    this.$apollo.mutate({
      mutation: SetShowHeaderInCache,
      variables: { show: true },
    });
  },
  methods: {
    openCreateModal() {
      this.selectedSnippet = null;
      this.duplicateSource = null;
      this.modalOpen = true;
    },
    openEditModal(snippet) {
      this.selectedSnippet = snippet;
      this.duplicateSource = null;
      this.modalOpen = true;
    },
    openDuplicateModal(snippet) {
      this.selectedSnippet = null;
      this.duplicateSource = snippet;
      this.modalOpen = true;
    },
    onSnippetSaved() {
      this.$apollo.queries.snippets.refetch();
    },
    async confirmDelete(snippet) {
      const ok = await this.$bvModal.msgBoxConfirm(
        `Delete snippet "${snippet.name}"? Favorites and tag assignments will be removed too.`,
        { okVariant: 'danger', okTitle: 'Delete' },
      );
      if (!ok) return;
      try {
        await this.$apollo.mutate({
          mutation: DeleteSnippetV2,
          variables: { snippetId: snippet.snippet_id },
        });
        this.$apollo.queries.snippets.refetch();
      } catch (error) {
        console.error('[AdminSnippets] Error deleting snippet:', error);
        this.$bvToast.toast('Could not delete snippet.', {
          variant: 'danger', solid: true, autoHideDelay: 4000,
        });
      }
    },
  },
};
</script>
